import { useState } from "react";
import { Heart, ChevronDown, ChevronUp } from "lucide-react";
import founderSoham from "@/assets/founder-soham-new.jpg";
import founderGourang from "@/assets/founder-gourang-new.jpg";
import { useIsMobile } from "@/hooks/use-mobile";

const FounderMessage = () => {
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  return (
    <section className="py-16 md:py-24 bg-background relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-secondary/5" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-6 md:mb-8 animate-fade-in">
            <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-full bg-accent/10 mb-4 md:mb-6 animate-pulse">
              <Heart className="w-6 h-6 md:w-8 md:h-8 text-accent" />
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
              From one student to another.
            </h2>
          </div>

          <div className="bg-card rounded-3xl p-6 md:p-8 lg:p-12 shadow-card hover:shadow-xl transition-all duration-500 animate-fade-in" style={{ animationDelay: '0.1s' }}>
            <blockquote className="space-y-4 md:space-y-6">
              {/* First paragraph */}
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                {isMobile && !expanded ? (
                  "We came to Australia as international students and experienced firsthand the challenges of settling in a new country."
                ) : (
                  "We came to Australia as students - confused and lost. That's why we started Ace Anchor: to make sure no student ever feels that way again."
                )}
              </p>
              
              {/* Second paragraph - show on desktop or when expanded on mobile */}
              {(!isMobile || expanded) && (
                <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                  We're not just a service - we're your family away from home. Every plan we've designed, 
                  every feature we offer, comes from our own experiences of what we wished someone had done for us.
                </p>
              )}
              
              {/* Third paragraph */}
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                {isMobile && !expanded ? (
                  "That's why we created Ace Anchor - to be the support system we wish we had when we first arrived."
                ) : (
                  "Your success in Australia is our success. Your peace of mind is our mission."
                )}
              </p>
            </blockquote>

            {/* Read More button for mobile */}
            {isMobile && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="flex items-center gap-2 px-4 py-2 text-primary hover:text-primary/80 transition-colors font-medium"
                >
                  {expanded ? (
                    <>
                      Read Less <ChevronUp className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      Read More <ChevronDown className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            )}

            <div className="mt-6 md:mt-8 pt-6 md:pt-8 border-t border-border">
              <div className="flex flex-col md:flex-row md:items-center md:justify-center gap-6 md:gap-12">
                <div className="flex items-center gap-4 hover:scale-105 transition-transform duration-300 group">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-accent/20 to-primary/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300" />
                    <img 
                      src={founderSoham} 
                      alt="Soham Gulati - Founder of Ace Anchor" 
                      className="relative w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-2 border-accent/20 shadow-md"
                    />
                  </div>
                  <div>
                    <p className="text-lg md:text-xl font-semibold text-foreground">Soham Gulati</p>
                    <p className="text-sm md:text-base text-muted-foreground">
                      {isMobile && !expanded ? "Founder" : "Founder & Former International Student"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 hover:scale-105 transition-transform duration-300 group">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300" />
                    <img 
                      src={founderGourang} 
                      alt="Gourang Gosain - Co-Founder of Ace Anchor" 
                      className="relative w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-2 border-accent/20 shadow-md"
                    />
                  </div>
                  <div>
                    <p className="text-lg md:text-xl font-semibold text-foreground">Gourang Gosain</p>
                    <p className="text-sm md:text-base text-muted-foreground">
                      {isMobile && !expanded ? "Co-Founder" : "Co-Founder & Former International Student"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 md:mt-12 text-center animate-fade-in" style={{ animationDelay: '0.3s' }}>
            <p className="text-base md:text-lg text-muted-foreground italic">
              "Every journey deserves a safe landing."
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FounderMessage;
